import { FC, useCallback, useEffect, useRef, useState } from 'react';

import MuiFormControl from '@material-ui/core/FormControl';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import MuiFormLabel from '@material-ui/core/FormLabel';
import MuiRadio from '@material-ui/core/Radio';
import MuiRadioGroup from '@material-ui/core/RadioGroup';
import { useField } from '@unform/core';

import { InputRef, RadioGroupProps } from './interfaces';

const RadioGroup: FC<RadioGroupProps> = ({
  name,
  options,
  onChange,
  label,
  radioProps,
  controlLabelProps,
  disabled,
  ...rest
}) => {
  const radioGroupRef = useRef<InputRef>(null);

  const {
    fieldName,
    registerField,
    error,
    defaultValue = '',
    clearError,
  } = useField(name);

  const [selectedValue, setSelectedValue] = useState(defaultValue ?? '');

  useEffect(() => {
    registerField<string>({
      name: fieldName,
      ref: radioGroupRef,
      getValue: (ref) => {
        return ref.current.value;
      },
      setValue: (_, value) => {
        setSelectedValue(value ?? '');
        clearError();
      },
      clearValue: (_, newValue) => {
        setSelectedValue(newValue ?? '');
        clearError();
      },
    });
  }, [fieldName, registerField, clearError]);

  useEffect(() => {
    if (defaultValue) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (radioGroupRef.current) {
      radioGroupRef.current.value = selectedValue;

      if (onChange) onChange(selectedValue);
    }
  }, [selectedValue, onChange]);

  const handleChange = useCallback(
    (e) => {
      const { value } = e.target;

      clearError();

      setSelectedValue(value);
    },
    [clearError],
  );

  return (
    <MuiFormControl component="fieldset" error={!!error}>
      {label && <MuiFormLabel component="legend">{label}</MuiFormLabel>}
      <MuiRadioGroup
        ref={radioGroupRef}
        name={fieldName}
        value={selectedValue}
        onChange={handleChange}
        {...rest}
      >
        {options.map((option) => (
          <MuiFormControlLabel
            {...controlLabelProps}
            key={option.key}
            value={option.value}
            control={<MuiRadio color="primary" {...radioProps} />}
            label={option.label}
            disabled={disabled}
          />
        ))}
      </MuiRadioGroup>

      {error && <MuiFormHelperText>{error}</MuiFormHelperText>}
    </MuiFormControl>
  );
};

export default RadioGroup;
